import { IS_PROD_ZONE } from '@/constants/env';
import { AdropParamsFactory } from '@/entities/advertisement/model';
import { AdPageType } from '@/models/ad/list';
import { Client } from '@/types/app';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

interface GoogleAdSenseProps {
  client: Client;
  adPageType: AdPageType;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  className?: string;
  wrapperClassName?: string;
  responsive?: boolean;
  style?: React.CSSProperties;
}

const ADSENSE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_CLIENT_ID;
const ADSENSE_SCRIPT_LOADED = 'adsense-script-loaded';

const loadAdSenseScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ('adsbygoogle' in window || document.querySelector(`[data-${ADSENSE_SCRIPT_LOADED}]`)) {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[src*="adsbygoogle.js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.body.setAttribute(`data-${ADSENSE_SCRIPT_LOADED}`, 'true');

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('AdSense script loading failed'));

    document.head.appendChild(script);
  });
};

const GoogleAdSense: React.FC<GoogleAdSenseProps> = (props) => {
  const router = useRouter();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [adKey, setAdKey] = useState(0);
  const adRef = useRef<HTMLModElement>(null);
  const initializationAttempted = useRef(false);
  const isMounted = useRef(true);
  const currentPath = useRef(router.asPath.split('?')[0]);

  const factory = new AdropParamsFactory(props.adPageType, props.client);
  const adSlot = factory.getGoogleAdSenseSlot();

  const recreateAd = () => {
    setAdKey((prev) => prev + 1);
    initializationAttempted.current = false;
  };

  const initializeAd = async () => {
    if (!isMounted.current || !isScriptLoaded || initializationAttempted.current || !adRef.current) {
      return;
    }

    initializationAttempted.current = true;

    try {
      if (adRef.current.children.length > 0) {
        return;
      }

      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      console.warn('AdSense initialization error:', error);
      if (isMounted.current) {
        initializationAttempted.current = false;
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    currentPath.current = router.asPath.split('?')[0];

    const loadScript = async () => {
      if (!isMounted.current) return;

      try {
        await loadAdSenseScript();
        if (isMounted.current) {
          setIsScriptLoaded(true);
        }
      } catch (error) {
        console.warn('AdSense script loading error:', error);
      }
    };

    loadScript();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isScriptLoaded && isMounted.current) {
      initializeAd();
    }
  }, [isScriptLoaded, adKey]);

  useEffect(() => {
    const purePath = router.asPath.split('?')[0];
    if (purePath !== currentPath.current && isScriptLoaded && isMounted.current) {
      currentPath.current = purePath;
      recreateAd();
    }
  }, [router.asPath, isScriptLoaded]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      initializationAttempted.current = false;
    };
  }, []);

  if (!ADSENSE_CLIENT_ID) {
    console.warn('Google AdSense Client ID is not configured');
    return null;
  }

  if (!adSlot) {
    return null;
  }

  const adsenseElement = (
    <ins
      key={`adsense-${adKey}`}
      ref={adRef}
      className={`adsbygoogle ${props.className || ''}`}
      style={{
        display: 'block',
        ...(props.style || {}),
      }}
      data-ad-client={ADSENSE_CLIENT_ID}
      data-ad-slot={adSlot}
      {...(props.responsive && { 'data-ad-format': 'auto' })}
      data-full-width-responsive={props.responsive ? 'true' : 'false'}
      data-adtest={!IS_PROD_ZONE ? 'on' : 'off'}
    />
  );

  if (props.wrapperClassName) {
    return <div className={props.wrapperClassName}>{adsenseElement}</div>;
  }

  return adsenseElement;
};

export default GoogleAdSense;
